import { Component } from "react";
import PropTypes from "prop-types";
import StackTrace from "stacktrace-js";

const noop = () => {};

const NOT_DEFINED = "NOT_DEFINED";

const getStackFrame = (error) =>
  StackTrace.fromError(error)
    .then((stackFrames) => stackFrames[0])
    .catch(() => ({}));

class RuntimeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.restore = () => this.setState({ hasError: false, error: null });
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    getStackFrame(error).then(({ fileName = "", lineNumber, columnNumber }) => {
      this.props.trackRuntimeError({
        blockableFlow: !!this.props.blockableFlow,
        errorMessage: error?.message ?? "",
        fileName: `${fileName}` || NOT_DEFINED,
        boundaryName: this.props.name || "noNameDefined",
        line: lineNumber || 1,
        column: columnNumber || 1,
        errorStackTrace: error.stack || NOT_DEFINED,
      });
    });
    this.props.onError();
  }

  componentDidMount() {
    this.props.onRestore(this.restore);
  }

  componentWillUnmount() {
    this.props.stopOnRestore();
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

RuntimeErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  namespace: PropTypes.string,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  onRestore: PropTypes.func,
  stopOnRestore: PropTypes.func,
  blockableFlow: PropTypes.bool,
  trackRuntimeError: PropTypes.func,
  launchBrokenFlow: PropTypes.func,
};

RuntimeErrorBoundary.defaultProps = {
  fallback: null,
  onError: noop,
  onRestore: noop,
  stopOnRestore: noop,
};

export default RuntimeErrorBoundary;
